import { Injectable } from '@angular/core';
import { StateCreator, StoreApi } from 'zustand/vanilla';
import { LockerApiService } from '../../api/locker-api.service';
import { LockerModel } from '../../shared/locker.model';
import { AssignLockerInput, GetLockerOutput } from '../../api/locker-api.types';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Locker } from '../../api/locker-api.class';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

export interface LockerStoreState {
  activeRequestLockers: LockerModel[] | [];
  activeLocker: Locker | null;
  activeLockers: GetLockerOutput[] | [];
}

export interface LockerStoreSlice {
  lockers: LockerStoreState;
}

export interface LockersStoreActions {
  assignLockers: () => void;
  assignLocker: (lockerId: number, input: AssignLockerInput) => Promise<void>;
  releaseLocker: (lockerId: number) => void;
  replaceLock: (lockerId: number) => Promise<void>;
  setActiveRequestLockers: (activeRequestLockers: LockerModel[]) => void;
  setActiveLocker: (id: number) => Promise<void>;
  setActiveLockers: () => Promise<void>;
}

const initialLockerStoreState: LockerStoreState = {
  activeRequestLockers: [],
  activeLocker: null,
  activeLockers: [],
};

@Injectable()
export class LockerStoreService {
  private slice: StateCreator<LockerStoreSlice>;
  private actions: (store: StoreApi<LockerStoreSlice>) => LockersStoreActions;

  constructor(lockerApiService: LockerApiService, message: NzMessageService) {
    this.slice = () => ({
      lockers: initialLockerStoreState,
    });
    this.actions = (store) => ({
      assignLockers() {
        const lockers = store.getState().lockers.activeRequestLockers;
        for (const locker of lockers) {
          lockerApiService.assignLocker(locker.id, {
            spindpersonId: locker.spindperson.id,
          });
        }
      },
      async assignLocker(
        lockerId: number,
        input: AssignLockerInput,
      ): Promise<void> {
        try {
          await lockerApiService.assignLocker(lockerId, input);
          await lockerApiService.handoutLockerKey(lockerId, {
            ausgabedatum: DateTime.now().toISODate(),
          });
          const activeLocker = await lockerApiService.getLocker(lockerId);

          store.setState((state) => ({
            lockers: { ...state.lockers, activeLocker },
          }));
          message.success('Spind erfolgreich zugewiesen!');
        } catch (error) {
          message.error('Etwas beim Zuweisen ist schiefgelaufen.');
          console.error(error);
        }
      },
      async releaseLocker(lockerId: number) {
        try {
          await lockerApiService.withdrawLockerKey(lockerId, {
            ruecknahmedatum: DateTime.now().toISODate(),
          });
          await lockerApiService.releaseLocker(lockerId);
          const activeLocker = await lockerApiService.getLocker(lockerId);

          store.setState((state) => ({
            lockers: { ...state.lockers, activeLocker },
          }));
          message.success('Spind erfolgreich freigegeben!');
        } catch (error) {
          message.error('Etwas beim Freigeben ist schiefgelaufen.');
          console.error(error);
        }
      },

      async replaceLock(lockerId: number) {
        try {
          await lockerApiService.replaceLock(lockerId, {
            ersatzdatum: DateTime.now().toISODate(),
          });
          const pastLocker = await lockerApiService.getLocker(lockerId);

          if (pastLocker.person) {
            await lockerApiService.releaseLocker(lockerId);
          }
          const activeLocker = await lockerApiService.getLocker(lockerId);

          store.setState((state) => ({
            lockers: { ...state.lockers, activeLocker },
          }));
          message.success(
            'Austausch des Schlosses wurde vermerkt. Spind kann wieder vergeben werden.',
          );
        } catch {
          message.error(
            'Etwas beim Vermerken des Austausches des Schlosses ist schiefgelaufen',
          );
        }
      },

      setActiveRequestLockers(activeRequestLockers) {
        store.setState((state) => ({
          lockers: { ...state.lockers, activeRequestLockers },
        }));
      },

      async setActiveLocker(id): Promise<void> {
        const activeLocker = await lockerApiService.getLocker(id);

        store.setState((state) => ({
          lockers: { ...state.lockers, activeLocker },
        }));
      },

      async setActiveLockers(): Promise<void> {
        try {
          const activeLockers = await firstValueFrom(
            lockerApiService.getAllLockers({}),
          );

          store.setState((state) => ({
            lockers: { ...state.lockers, activeLockers },
          }));
        } catch {
          message.error('Etwas beim Laden der Spinde ist schiefgelaufen.');
        }
      },

      reset() {
        store.setState({ lockers: initialLockerStoreState });
      },
    });
  }

  getSlice() {
    return this.slice;
  }

  getActions() {
    return this.actions;
  }
}
